(() => {
  if (window.__preorderCartValidationLoaded) return;
  window.__preorderCartValidationLoaded = true;

  const CONFIG = {
    statusSectionId: 'preorder-cart-status',

    preorderMessage: 'Note: This is a preorder item. Estimated shipping: 45-60 days.',

    cartDrawerSelector: 'cart-drawer#cart-drawer',

    cartSectionsToRefresh: [
      'cart-drawer',
      'cart-icon-bubble'
    ],

    checkoutButtonSelector: [
      'button[name="checkout"]',
      'input[name="checkout"]',
      'a[href="/checkout"]',
      'a[href^="/checkout?"]',
      'a[href$="/checkout"]',
      'cart-drawer button[name="checkout"]',
      'cart-drawer input[name="checkout"]',
      'cart-drawer a[href="/checkout"]'
    ].join(','),

    stopCheckoutWhenAvailabilityChanged: true
  };

  let validationPromise = null;
  let checkoutContinueRequested = false;

  const preorderRegex = /pre[\s_-]*order/i;

  function root() {
    return window.Shopify?.routes?.root || '/';
  }

  function normalizeProperties(properties = {}) {
    const output = {};

    Object.entries(properties || {}).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      const stringValue = String(value).trim();

      if (stringValue === '') return;

      output[key] = stringValue;
    });

    return output;
  }

  function isPreorderProperty(key, value) {
    const keyText = String(key || '');
    const valueText = String(value || '');

    return preorderRegex.test(keyText) || preorderRegex.test(valueText);
  }

  function hasAnyPreorderProperty(properties = {}) {
    return Object.entries(normalizeProperties(properties)).some(([key, value]) => {
      return isPreorderProperty(key, value);
    });
  }

  function hasOfficialPreorderMarker(properties = {}) {
    const props = normalizeProperties(properties);

    return (
      String(props._preorder || '').toLowerCase() === 'true' ||
      Boolean(props._preorder_short) ||
      Boolean(props.Preorder)
    );
  }

  function removePreorderProperties(properties = {}) {
    const cleaned = {};

    Object.entries(normalizeProperties(properties)).forEach(([key, value]) => {
      if (!isPreorderProperty(key, value)) {
        cleaned[key] = value;
      }
    });

    return cleaned;
  }

  function buildExpectedProperties(currentProperties, variantStatus) {
    const normalized = normalizeProperties(currentProperties);
    const message = variantStatus?.short_message || CONFIG.preorderMessage;

    if (variantStatus?.is_preorder) {
      return {
        ...normalized,

        _preorder: 'true',
        _preorder_short: message,
        Preorder: message
      };
    }

    return removePreorderProperties(normalized);
  }

  function sameProperties(a = {}, b = {}) {
    const propsA = normalizeProperties(a);
    const propsB = normalizeProperties(b);

    const keysA = Object.keys(propsA).sort();
    const keysB = Object.keys(propsB).sort();

    if (keysA.length !== keysB.length) return false;

    return keysA.every((key, index) => {
      const compareKey = keysB[index];

      return key === compareKey && String(propsA[key]) === String(propsB[compareKey]);
    });
  }

  function isEmptyObject(object = {}) {
    return Object.keys(object || {}).length === 0;
  }

  async function fetchCart() {
    const response = await fetch(`${root()}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to fetch cart.');
    }

    return response.json();
  }

  async function fetchPreorderStatusMap() {
    const response = await fetch(`${root()}?sections=${CONFIG.statusSectionId}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to fetch preorder status section.');
    }

    const sections = await response.json();
    const sectionHtml = sections[CONFIG.statusSectionId];

    if (!sectionHtml) {
      throw new Error(`Missing section: ${CONFIG.statusSectionId}`);
    }

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const jsonEl = doc.querySelector('[data-preorder-cart-status]');

    if (!jsonEl) {
      throw new Error('Missing preorder cart status JSON.');
    }

    const parsed = JSON.parse(jsonEl.textContent);

    return parsed.variants || {};
  }

  async function changeCartLine(payload) {
    const response = await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Unable to update cart line.');
    }

    return response.json();
  }

  async function addCartItem(item, properties) {
    const addItem = {
      id: item.variant_id,
      quantity: item.quantity
    };

    if (properties && !isEmptyObject(properties)) {
      addItem.properties = properties;
    }

    if (item.selling_plan_allocation?.selling_plan?.id) {
      addItem.selling_plan = item.selling_plan_allocation.selling_plan.id;
    }

    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [addItem]
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Unable to add cart item.');
    }

    return response.json();
  }

  async function updateLineProperties(item, expectedProperties, forceRemoveAndReAdd = false) {
    if (forceRemoveAndReAdd || isEmptyObject(expectedProperties)) {
      await changeCartLine({
        id: item.key,
        quantity: 0
      });

      await addCartItem(item, expectedProperties);

      return;
    }

    await changeCartLine({
      id: item.key,
      quantity: item.quantity,
      properties: expectedProperties
    });
  }

  async function refreshCartUI() {
    const currentDrawer = document.querySelector(CONFIG.cartDrawerSelector);
    const drawerWasOpen = currentDrawer?.hasAttribute('open');

    const sectionIds = CONFIG.cartSectionsToRefresh.filter(Boolean);

    if (!sectionIds.length) return;

    const response = await fetch(`${root()}?sections=${sectionIds.join(',')}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (response.ok) {
      const sections = await response.json();

      if (sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');

        const newDrawer =
          doc.querySelector(CONFIG.cartDrawerSelector) ||
          doc.querySelector('#cart-drawer') ||
          doc.querySelector('cart-drawer');

        if (currentDrawer && newDrawer) {
          if (drawerWasOpen) {
            newDrawer.setAttribute('open', '');
            newDrawer.style.display = 'block';
          }

          currentDrawer.replaceWith(newDrawer);
        }
      }

      if (sections['cart-icon-bubble']) {
        const currentBubble =
          document.getElementById('shopify-section-cart-icon-bubble') ||
          document.querySelector('#cart-icon-bubble') ||
          document.querySelector('[id*="cart-icon-bubble"]');

        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');

        const newBubble =
          doc.getElementById('shopify-section-cart-icon-bubble') ||
          doc.querySelector('#cart-icon-bubble') ||
          doc.querySelector('[id*="cart-icon-bubble"]');

        if (currentBubble && newBubble) {
          currentBubble.replaceWith(newBubble);
        }
      }
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          source: 'preorder-cart-validation'
        }
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: {
          source: 'preorder-cart-validation'
        }
      })
    );

    setupCartDrawerObserver();
  }

  async function validatePreorderCartInner() {
    const cart = await fetchCart();

    if (!cart.items || !cart.items.length) {
      return {
        changed: false,
        availabilityChanged: false,
        changes: []
      };
    }

    const preorderStatusMap = await fetchPreorderStatusMap();

    let changed = false;
    let availabilityChanged = false;
    const changes = [];

    for (let index = cart.items.length - 1; index >= 0; index--) {
      const item = cart.items[index];
      const variantStatus = preorderStatusMap[String(item.variant_id)];

      if (!variantStatus) continue;

      const currentProperties = normalizeProperties(item.properties);
      const shouldBePreorder = Boolean(variantStatus.is_preorder);

      const currentlyMarkedPreorder = hasOfficialPreorderMarker(currentProperties);
      const lineAvailabilityChanged = currentlyMarkedPreorder !== shouldBePreorder;

      const expectedProperties = buildExpectedProperties(currentProperties, variantStatus);

      if (!sameProperties(currentProperties, expectedProperties)) {
        const shouldForceRemoveAndReAdd =
          !shouldBePreorder && hasAnyPreorderProperty(currentProperties);

        await updateLineProperties(item, expectedProperties, shouldForceRemoveAndReAdd);

        changed = true;

        if (lineAvailabilityChanged) {
          availabilityChanged = true;
        }

        changes.push({
          title: item.product_title || item.title,
          variant_id: item.variant_id,
          was_preorder: currentlyMarkedPreorder,
          is_preorder: shouldBePreorder,
          availability_changed: lineAvailabilityChanged
        });
      }
    }

    if (changed) {
      await refreshCartUI();
    }

    return {
      changed,
      availabilityChanged,
      changes
    };
  }

  async function validatePreorderCart() {
    if (validationPromise) {
      return validationPromise;
    }

    validationPromise = validatePreorderCartInner()
      .finally(() => {
        validationPromise = null;
      });

    return validationPromise;
  }

  function showCartChangedMessage(result) {
    const hasPreorderAdded = result?.changes?.some((change) => {
      return change.availability_changed && change.is_preorder;
    });

    const hasPreorderRemoved = result?.changes?.some((change) => {
      return change.availability_changed && !change.is_preorder;
    });

    let message = 'We updated your cart because product availability changed. Please review your cart before checkout.';

    if (hasPreorderAdded && !hasPreorderRemoved) {
      message = 'Some item availability changed and has been updated as preorder. Please review your cart before checkout.';
    }

    if (!hasPreorderAdded && hasPreorderRemoved) {
      message = 'Some preorder item availability changed and is now available. We updated your cart. Please review it before checkout.';
    }

    alert(message);
  }

  function setCheckoutButtonLoading(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.dataset.originalText = button.tagName === 'INPUT'
        ? button.value
        : button.textContent;

      button.setAttribute('aria-busy', 'true');

      if ('disabled' in button) {
        button.disabled = true;
      }

      if (button.tagName === 'INPUT') {
        button.value = 'Checking cart...';
      } else {
        button.textContent = 'Checking cart...';
      }

      return;
    }

    button.removeAttribute('aria-busy');

    if ('disabled' in button) {
      button.disabled = false;
    }

    if (button.dataset.originalText) {
      if (button.tagName === 'INPUT') {
        button.value = button.dataset.originalText;
      } else {
        button.textContent = button.dataset.originalText;
      }

      delete button.dataset.originalText;
    }
  }

  function continueToCheckoutFromButton(button) {
    const form = button?.closest('form');

    if (form) {
      checkoutContinueRequested = true;

      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = 'Checkout';

      form.appendChild(checkoutInput);
      form.submit();

      return;
    }

    window.location.href = `${root()}checkout`;
  }

  async function handleCheckoutClick(event) {
    const button = event.target.closest(CONFIG.checkoutButtonSelector);

    if (!button) return;

    if (checkoutContinueRequested) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    setCheckoutButtonLoading(button, true);

    try {
      const result = await validatePreorderCart();

      if (result.availabilityChanged && CONFIG.stopCheckoutWhenAvailabilityChanged) {
        showCartChangedMessage(result);
        return;
      }

      continueToCheckoutFromButton(button);
    } catch (error) {
      console.error('[Preorder cart validation]', error);

      alert('We could not verify your cart availability. Please refresh the cart and try again.');

      await refreshCartUI();
    } finally {
      setCheckoutButtonLoading(button, false);
    }
  }

  async function handleCartSubmit(event) {
    const form = event.target;

    if (!form || checkoutContinueRequested) return;

    const submitter = event.submitter;

    const isCheckoutSubmit =
      submitter?.matches?.(CONFIG.checkoutButtonSelector) ||
      form.querySelector('[name="checkout"]');

    if (!isCheckoutSubmit) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    setCheckoutButtonLoading(submitter, true);

    try {
      const result = await validatePreorderCart();

      if (result.availabilityChanged && CONFIG.stopCheckoutWhenAvailabilityChanged) {
        showCartChangedMessage(result);
        return;
      }

      checkoutContinueRequested = true;
      form.submit();
    } catch (error) {
      console.error('[Preorder cart validation]', error);

      alert('We could not verify your cart availability. Please refresh the cart and try again.');

      await refreshCartUI();
    } finally {
      setCheckoutButtonLoading(submitter, false);
    }
  }

  function setupCartDrawerObserver() {
    const drawer = document.querySelector(CONFIG.cartDrawerSelector);

    if (!drawer || drawer.dataset.preorderObserver === 'true') return;

    drawer.dataset.preorderObserver = 'true';

    let validationTimer;

    const observer = new MutationObserver(() => {
      if (!drawer.hasAttribute('open')) return;

      clearTimeout(validationTimer);

      validationTimer = setTimeout(() => {
        validatePreorderCart().catch((error) => {
          console.error('[Preorder cart validation]', error);
        });
      }, 250);
    });

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['open']
    });
  }

  document.addEventListener('click', handleCheckoutClick, true);
  document.addEventListener('submit', handleCartSubmit, true);

  document.addEventListener('DOMContentLoaded', () => {
    setupCartDrawerObserver();

    if (window.location.pathname.includes('/cart')) {
      validatePreorderCart().catch((error) => {
        console.error('[Preorder cart validation]', error);
      });
    }
  });

  window.validatePreorderCart = validatePreorderCart;
})();